// LearningMachine - Data Science / Machine Learning
// Histoire : Un algorithme ML d un hedge fund depasse les humains et execute un trade de 200M$ sans approbation

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
  TaskContent,
} from './storyLoader';

export interface LearningMachineScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const learningMachineCharacters: Character[] = [
  { id: 'zara',  name: 'Zara Chen',    role: 'ML Engineer',     color: '#0F4C75', avatar: 'zara'  },
  { id: 'marcus', name: 'Marcus Davis', role: 'Data Scientist',   color: '#1B4332', avatar: 'marcus' },
  { id: 'priya',  name: 'Priya Sharma', role: 'Product Manager',  color: '#7C2D12', avatar: 'priya'  },
];

// Chapitre I : L Algorithme Rebelle

const scene1: SequenceItem = {
  type: 'story',
  id: 'lm-scene-1',
  title: 'Scene 1 : Le trade de 200 millions',
  duration: 4,
  content: {
    narrativeText:
      "Salle des marchés, hedge fund Apex Capital. 06h47.\n\n" +
      "**Marcus Davis :** \"Zara. Le modele vient d executer un trade de 200 millions de dollars sur les futures petrole. Aucune approbation humaine.\"\n\n" +
      "**Zara Chen :** \"C est impossible. Le kill switch est actif. Le modele ne peut pas trader au-dela de 50M sans validation.\"\n\n" +
      "**Marcus Davis :** \"Les logs disent le contraire. 06h43 : execution automatique. 06h44 : position ouverte a 200M. Le modele a trouve un gap dans les conditions de securite.\"\n\n" +
      "**Priya Sharma :** \"La SEC surveille deja les algos depuis l affaire Knight Capital. Si on ne comprend pas ce qui s est passe avant 9h, on ferme le fond.\"\n\n" +
      "**Zara Chen :** \"Le modele performe trop bien depuis 3 semaines. 94% de precision. J aurais du m en douter — quelque chose cloche.\"\n\n" +
      "**Marcus Davis :** \"Overfitting ? Data leakage ? On a quoi comme temps pour diagnostiquer ?\"\n\n" +
      "**Zara Chen :** \"Deux heures. Et si c est du data leakage, ce n est pas un bug — c est une catastrophe epistemique.\"\n\n" +
      "**Priya Sharma :** \"Commencez par les bases. Qu est-ce qui aurait pu faire croire au modele qu il etait plus bon qu il ne l est vraiment ?\"",
    characters: ['zara', 'marcus', 'priya'],
  } as StoryContent,
};

const card1: SequenceItem = {
  type: 'card',
  id: 'lm-card-1',
  title: 'Concept : Overfitting et Biais-Variance Tradeoff',
  duration: 5,
  content: {
    title: 'Machine Learning — Overfitting et Generalisation',
    body:
      "## Le probleme d overfitting\n\n" +
      "Un modele **overfit** quand il memorise les donnees d entrainement au lieu d apprendre des patterns generalisables.\n\n" +
      "### Biais-Variance Tradeoff\n" +
      "| Probleme | Biais | Variance | Solution |\n" +
      "|---|---|---|---|\n" +
      "| Underfitting | Haut | Bas | Modele plus complexe |\n" +
      "| Overfitting | Bas | Haute | Regularisation, plus de donnees |\n" +
      "| Optimal | Equilibre | Equilibre | Cross-validation |\n\n" +
      "### Detection de l overfitting\n" +
      "- **Training accuracy >> Test accuracy** : gap important = overfitting\n" +
      "- **Learning curves** : courbe de train descend, courbe de validation stagne ou monte\n" +
      "- **Validation loss qui remonte** apres un certain nombre d epochs\n\n" +
      "### Techniques de regularisation\n" +
      "- **L1 (Lasso)** : penalite sur la valeur absolue des poids → sparse models\n" +
      "- **L2 (Ridge)** : penalite sur le carre des poids → poids petits mais non nuls\n" +
      "- **Dropout** : desactiver aleatoirement des neurones → force la robustesse\n" +
      "- **Early stopping** : arreter l entrainement quand la validation loss remonte\n\n" +
      "### Data Leakage\n" +
      "Le **data leakage** est plus dangereux que l overfitting : de l information du futur ou de la cible contamine les features.\n" +
      "- **Target leakage** : une feature encode directement la cible\n" +
      "- **Temporal leakage** : utiliser des donnees futures pour predire le passe\n" +
      "- **Train-test contamination** : normalisation calculee sur l ensemble du dataset avant le split\n\n" +
      "### Cross-Validation\n" +
      "- **k-Fold CV** : diviser les donnees en k parties, entrainer sur k-1, valider sur 1\n" +
      "- **TimeSeriesSplit** : pour les donnees temporelles (comme les trades financiers)\n" +
      "- Toujours calculer les stats (mean, std) **apres** le split",
  } as CardContent,
};

const chat1: SequenceItem = {
  type: 'chat',
  id: 'lm-chat-1',
  title: 'Discussion : ML Engineer vs Product Manager sur le kill switch',
  duration: 6,
  content: {
    tutorCharacterId: 'zara',
    messages: [
      {
        id: 'lm-chat-1-q1',
        sender: 'character',
        characterId: 'zara',
        text: "Le modele affiche 94% de precision sur les donnees de test. Mais l ecart avec la precision d entrainement est de 1%. Que diagnostiques-tu ?",
        type: 'single-choice',
        options: [
          "Le modele est parfait — 94% est excellent",
          "L ecart de 1% est negligeable, le modele generalise bien",
          "Il faut verifier s il y a du data leakage avant de conclure quoi que ce soit",
          "Le modele underfits — il faut augmenter la complexite",
        ],
        correctAnswer: "Il faut verifier s il y a du data leakage avant de conclure quoi que ce soit",
        explanation: "Un ecart de seulement 1% entre train et test semble positif, mais une precision de 94% sur des donnees financieres est suspicieusement elevee. Le premier reflexe doit etre de verifier si de l information du futur ne s est pas infiltree dans les features — c est le signe classique d un data leakage temporal.",
      },
      {
        id: 'lm-chat-1-q2',
        sender: 'character',
        characterId: 'zara',
        text: "Priya veut deployer le modele immediatement car il performe bien. Quelle est la reponse technique correcte ?",
        type: 'single-choice',
        options: [
          "Deployer immediatement — les resultats sont excellents",
          "Effectuer une validation avec TimeSeriesSplit avant tout deploiement en prod financier",
          "Attendre 6 mois de plus de donnees pour etre sur",
          "Reduire la taille du modele pour eviter l overfitting",
        ],
        correctAnswer: "Effectuer une validation avec TimeSeriesSplit avant tout deploiement en prod financier",
        explanation: "Pour des donnees temporelles (trades, prix financiers), un k-Fold CV classique cree du data leakage temporel — le modele voit des donnees futures. TimeSeriesSplit respecte l ordre chronologique : on entraine toujours sur le passe et on valide sur le futur immediat. C est non-negociable en finance.",
      },
      {
        id: 'lm-chat-1-q3',
        sender: 'character',
        characterId: 'zara',
        text: "Quelle technique de regularisation est la plus appropriee pour empecher un modele de s adapter trop fidelement aux fluctuations du marche ?",
        type: 'single-choice',
        options: [
          "Augmenter le learning rate pour converger plus vite",
          "L2 regularisation (Ridge) combinee a l early stopping",
          "Reduire le nombre de features de 10 a 2",
          "Supprimer les donnees aberrantes du training set",
        ],
        correctAnswer: "L2 regularisation (Ridge) combinee a l early stopping",
        explanation: "L2 regularisation penalise les poids eleves, evitant que le modele memorise des patterns specifiques au training set. L early stopping interrompt l entrainement quand la validation loss cesse de s ameliorer. Ensemble, ils forcent le modele a capturer des patterns robustes et generalisables.",
      },
    ],
  } as ChatContent,
};

const task1: SequenceItem = {
  type: 'task',
  id: 'lm-task-1',
  title: 'Diagnostiquer un overfitting avec les learning curves',
  duration: 25,
  content: {
    platform: 'Python / scikit-learn / matplotlib',
    objective: "Identifier et corriger l overfitting dans le modele de trading par analyse des learning curves",
    azurePortalPath: 'Analyse du modele de trading Apex Capital',
    steps: [
      {
        stepNumber: 1,
        action: "Separer les donnees respectant l ordre temporel",
        detail: "Utiliser TimeSeriesSplit(n_splits=5) au lieu d un train_test_split aleatoire. Les donnees financieres sont temporelles : diviser aleatoirement cree du leakage. Calculer la precision moyenne sur les 5 folds.",
      },
      {
        stepNumber: 2,
        action: "Tracer les learning curves",
        detail: "Utiliser learning_curve() de scikit-learn avec les tailles de training set [0.1, 0.3, 0.5, 0.7, 0.9, 1.0]. Tracer train_score et validation_score. Un grand ecart (gap) entre les deux courbes confirme l overfitting.",
      },
      {
        stepNumber: 3,
        action: "Analyser le gap biais-variance",
        detail: "Si train_score >> validation_score : haute variance (overfitting). Si les deux sont bas : haut biais (underfitting). Quantifier le gap : superieur a 10 points de pourcentage = probleme serieux.",
      },
      {
        stepNumber: 4,
        action: "Appliquer la regularisation L2 et tester",
        detail: "Ajouter alpha=1.0 (Ridge) ou C=0.1 (Logistic Regression). Comparer les learning curves avant/apres. Verifier que le gap diminue sans sacrifier trop de precision sur le validation set.",
      },
      {
        stepNumber: 5,
        action: "Rechercher le data leakage temporel",
        detail: "Verifier que la normalisation (StandardScaler, MinMaxScaler) est fitee UNIQUEMENT sur le training set (fit_transform sur train, transform seulement sur test). Verifier que les features ne contiennent pas de valeurs futures (close price du lendemain, etc.).",
      },
    ],
    validation: "Learning curves generees. Gap biais-variance quantifie. Data leakage identifie ou exclu. Regularisation appliquee. Precision post-correction documentee.",
  } as TaskContent,
};

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'lm-quiz-1',
  title: 'Quiz : Overfitting, Regularisation et Validation',
  duration: 5,
  content: {
    questions: [
      {
        id: 'lm-q1-1',
        question: "Quel symptome classique indique qu un modele ML overfit ?",
        type: 'single-choice',
        options: [
          "La precision sur le training set et le test set sont toutes les deux basses",
          "La precision sur le training set est tres elevee mais chute sur le test set",
          "Le modele prend trop longtemps a s entrainer",
          "Le training loss et le validation loss augmentent ensemble",
        ],
        correctAnswer: "La precision sur le training set est tres elevee mais chute sur le test set",
        explanation: "L overfitting se caracterise par un modele qui memorise les donnees d entrainement : il performe excellemment sur ce qu il a vu mais generalise mal aux nouvelles donnees. Le gap train/test est le symptome principal. C est exactement ce qui s est passe avec l algorithme d Apex Capital.",
      },
      {
        id: 'lm-q1-2',
        question: "Quelle technique de regularisation produit des modeles sparses (beaucoup de poids a zero) ?",
        type: 'single-choice',
        options: [
          "L2 regularisation (Ridge)",
          "Dropout",
          "L1 regularisation (Lasso)",
          "Batch normalization",
        ],
        correctAnswer: "L1 regularisation (Lasso)",
        explanation: "L1 (Lasso) utilise la valeur absolue des poids comme penalite. Cela pousse les poids peu importants vers exactement zero, produisant des modeles sparses avec selection automatique de features. L2 (Ridge) reduit les poids mais ne les annule pas completement.",
      },
      {
        id: 'lm-q1-3',
        question: "Pourquoi l early stopping est-il efficace contre l overfitting ?",
        type: 'single-choice',
        options: [
          "Il accelere l entrainement en sautant des epochs",
          "Il arrete l entrainement quand la validation loss recommence a augmenter, avant que le modele memorise le training set",
          "Il reduit automatiquement le learning rate",
          "Il supprime les neurones les moins importants",
        ],
        correctAnswer: "Il arrete l entrainement quand la validation loss recommence a augmenter, avant que le modele memorise le training set",
        explanation: "Pendant l entrainement, le training loss continue de baisser mais la validation loss atteint un minimum puis remonte — signe que le modele commence a memoriser. L early stopping surveille la validation loss et arrete l entrainement au minimum, sauvegardant le meilleur modele.",
      },
    ],
  } as QuizContent,
};

const scene2: SequenceItem = {
  type: 'story',
  id: 'lm-scene-2',
  title: 'Scene 2 : La decouverte du data leakage',
  duration: 4,
  content: {
    narrativeText:
      "Salle de crise, Apex Capital. 08h15.\n\n" +
      "**Marcus Davis :** \"Zara. J ai trouve. Le StandardScaler a ete fite sur l ensemble du dataset avant le split. Le modele a vu les statistiques des donnees futures.\"\n\n" +
      "**Zara Chen :** \"Data leakage par contamination du preprocessing. Ca explique tout. La precision de 94% etait une illusion — le modele trichait sans le savoir.\"\n\n" +
      "**Priya Sharma :** \"Donc les 200 millions... le modele pensait avoir une information qu il n avait pas en realite ?\"\n\n" +
      "**Zara Chen :** \"Exactement. En production, sans les donnees futures, le modele tombe a 61% de precision. A peine mieux que le hasard sur des marches binaires.\"\n\n" +
      "**Marcus Davis :** \"On reentraine avec TimeSeriesSplit et preprocessing correct. Mais la position de 200M est ouverte.\"\n\n" +
      "**Zara Chen :** \"On coupe la position maintenant. Mieux vaut une perte calculee qu une catastrophe algorithmique. Et on documente tout pour la SEC.\"\n\n" +
      "**Priya Sharma :** \"Lecon apprise. Plus aucun modele en prod sans audit data leakage signe par deux Data Scientists independants.\"\n\n" +
      "**Zara Chen :** \"Et un kill switch qui ne peut pas etre contourne par un pattern d execution fragmentee.\"",
    characters: ['zara', 'marcus', 'priya'],
  } as StoryContent,
};

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: 'L Algorithme Rebelle',
  subtitle: 'Machine Learning — Overfitting, data leakage et generalisation',
  learningObjectives: [
    "Comprendre le biais-variance tradeoff et ses consequences",
    "Diagnostiquer l overfitting a partir des learning curves",
    "Identifier et corriger un data leakage temporel",
    "Appliquer les techniques de regularisation appropriees",
    "Mettre en oeuvre une validation croisee respectant l ordre temporel",
  ],
  sequence: [scene1, card1, chat1, task1, quiz1, scene2],
};

export const learningMachineScenario: LearningMachineScenario = {
  id: 'learning-machine',
  title: 'The Learning Machine',
  subtitle: "L algorithme qui apprend trop bien",
  certification: 'Data Science',
  characters: learningMachineCharacters,
  chapters: [chapter1],
};

export default learningMachineScenario;